import React, { useContext, useEffect, useState } from 'react';
import { AppBar, Toolbar, IconButton, Menu, MenuItem, Avatar, Typography, Badge, Drawer, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import { Link, useNavigate } from 'react-router-dom';
import { AppRoutes } from '../../../enums/Approutes';
import LogoBlack from '../../../assets/images/logo-black.png'
import { useTranslation } from 'react-i18next';
import { UserInfo } from '../../../interfaces/ProfileInfo';
import axios from 'axios';
import LanguageSwitcher from '../../../i18n/LanguageSwitcher';
import SettingsIcon from '@mui/icons-material/Settings';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import MapIcon from '@mui/icons-material/Map';
import ChatIcon from '@mui/icons-material/Chat';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import GroupIcon from '@mui/icons-material/Group';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ReferralIcon from '@mui/icons-material/PersonAdd';
import PublicIcon from '@mui/icons-material/Public';
import { PinnerProIcon } from '../../../assets/icons/Index';
import { useGlobalData } from '../../../hooks/globalDataContext';
import { addCreditsEventListener, CREDITS_EVENTS } from '../../../utils/creditsEvents';
import { AuthContext } from '../../../hooks/authContext';
import { getConfig } from '../../../config/env';

const Navbar = () => {
  const url = getConfig().BASE_URL;
  const authToken = sessionStorage.getItem('authToken');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [notificationRead, setNotificationRead] = useState<boolean>(false);
  const [userInfo, setUserInfo] = useState<UserInfo>({
    first_name: '',
    profile_picture: '',
    is_pinner_pro: false,
    id: 0,
  });
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logout } = useContext(AuthContext);

  // Utilizzo il GlobalDataContext per i crediti e notifiche
  const { credits, refreshCredits, unreadCount } = useGlobalData();

  useEffect(() => {
    const fetchUserData = async () => {
      const headers = {
        'Authorization': `Bearer ${authToken}`
      };
      try {
        const userResponse = await axios.get(
          `${url}/users/auth/profile/`, { headers }
        );
        const userData = userResponse.data;
        setUserInfo({
          first_name: userData.first_name,
          profile_picture: userData.profile_picture,
          is_pinner_pro: userData.is_pinner_pro,
          id: userData.id,
        });
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();

    // Listener per aggiornamenti dei crediti usando le nuove utility
    const removeCreditsListener = addCreditsEventListener(() => {
      refreshCredits(true);
    }, CREDITS_EVENTS.CREDITS_UPDATED);

    // Listener per aggiornamenti della foto profilo
    const handleProfilePictureUpdated = () => {
      fetchUserData();
    };

    // Aggiorna notificationRead quando cambiano le notifiche
    if (unreadCount > 0) {
      setNotificationRead(false);
    }

    window.addEventListener('profilePictureUpdated', handleProfilePictureUpdated);

    return () => {
      removeCreditsListener();
      window.removeEventListener('profilePictureUpdated', handleProfilePictureUpdated);
    };
  }, [authToken, refreshCredits, unreadCount]);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuOpen = () => {
    setMobileMenuOpen(true);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
  };

  const handleNotifications = () => {
    setNotificationRead(true);
    navigate(AppRoutes.NOTIFICATIONS);
  }

  const handleMobileNavigation = (route: string) => {
    navigate(route);
    setMobileMenuOpen(false);
  };

  const handleMobileNotifications = () => {
    setNotificationRead(true);
    navigate(AppRoutes.NOTIFICATIONS);
    setMobileMenuOpen(false);
  };

  const handleMobileLogout = () => {
    setMobileMenuOpen(false);
    logout();
  };

  const handleMobileBilling = () => {
    navigate(AppRoutes.BILLING);
    setMobileMenuOpen(false);
  };

  const handleConsultancy = () => {
    if (userInfo.id && userInfo.is_pinner_pro) {
      navigate(AppRoutes.CONSULTANCY.replace(':userId', userInfo.id.toString()));
    }
  };

  const handleMobileConsultancy = () => {
    if (userInfo.id && userInfo.is_pinner_pro) {
      navigate(AppRoutes.CONSULTANCY.replace(':userId', userInfo.id.toString()));
      setMobileMenuOpen(false);
    }
  };

  const menuId = 'primary-search-account-menu';
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      id={menuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={Boolean(anchorEl)}
      onClose={handleMenuClose}
      slotProps={{
        paper: {
          sx: {
            marginTop: '54px',
            width: '148px'
          }
        }
      }}
    >
      <MenuItem onClick={handleMenuClose}>
        <Link to={AppRoutes.SETTINGS} style={{ display: 'flex', alignItems: 'center' }}>
          <SettingsIcon fontSize="small" sx={{ marginRight: '0.25rem' }} />
          <p>{t('navbar:settings')}</p>
        </Link>
      </MenuItem>
      <MenuItem onClick={() => { handleLogout(); handleMenuClose(); }} className="flex items-center">
        <ExitToAppIcon fontSize="small" sx={{ marginRight: '0.25rem' }} />
        {t('navbar:logout')}
      </MenuItem>
      <MenuItem>
        <LanguageSwitcher className='text-sm md:text-base' />
      </MenuItem>
      <div style={{ marginTop: '0.10rem' }}>
        <p style={{ marginLeft: '1.1rem', fontSize: '0.55rem' }}>v0.3.7</p>
      </div>
    </Menu>
  );

  const mobileMenuId = 'primary-search-account-menu-mobile';
  const renderMobileMenu = (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={handleMobileMenuClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: '100vw',
          height: '100vh',
          backgroundColor: 'white',
          padding: 0,
        },
      }}
    >
      <div style={{ width: '100%', height: '100%' }}>

        <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '0.5rem' }}>
          <IconButton onClick={handleMobileMenuClose} size="medium">
            <CloseIcon />
          </IconButton>
        </div>

        <div style={{ width: '100%', display: 'flex', justifyContent: 'space-evenly', alignItems: 'center', padding: '0.5rem', gap: '1rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <IconButton onClick={handleMobileNotifications}>
              <Badge variant="dot" color="success" invisible={notificationRead || unreadCount === 0}>
                <NotificationsIcon sx={{ color: '#E9495A' }} />
              </Badge>
            </IconButton>
            <p>{t('navbar:notifications')}</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <IconButton onClick={handleMobileBilling}>
              <Typography variant="body1" sx={{ fontSize: '1rem', fontWeight: 'bold', color: '#1A1A1A' }}>{credits}</Typography>
            </IconButton>
            <p>{t('navbar:billing')}</p>
          </div>
        </div>

        {/* Menu items */}
        <List style={{ width: '100%', padding: '0.5rem', marginBottom: '2.5rem' }}>
          <ListItem
            component="button"
            sx={{ paddingBottom: '10px' }}
            onClick={() => handleMobileNavigation(AppRoutes.PIN_MAP)}
          >
            <ListItemIcon sx={{ minWidth: '35px' }}>
              <MapIcon />
            </ListItemIcon>
            <ListItemText
              primary={t('navbar:pinMap')}
              primaryTypographyProps={{
                fontSize: '1rem',
                fontWeight: 500,
              }}
            />
          </ListItem>

          <ListItem
            component="button"
            sx={{ paddingBottom: '10px' }}
            onClick={() => handleMobileNavigation(AppRoutes.PINNERS)}
          >
            <ListItemIcon sx={{ minWidth: '35px' }}>
              <GroupIcon />
            </ListItemIcon>
            <ListItemText
              primary="Pinners"
              primaryTypographyProps={{
                fontSize: '1rem',
                fontWeight: 500,
              }}
            />
          </ListItem>

          {userInfo.is_pinner_pro && (
            <ListItem
              component="button"
              sx={{ paddingBottom: '10px' }}
              onClick={handleMobileConsultancy}
            >
              <ListItemIcon sx={{ minWidth: '35px' }}>
                <PublicIcon />
              </ListItemIcon>
              <ListItemText
                primary={t('navbar:consultancy')}
                primaryTypographyProps={{
                  fontSize: '1rem',
                  fontWeight: 500,
                }}
              />
            </ListItem>
          )}

          <ListItem
            component="button"
            sx={{ paddingBottom: '10px' }}
            onClick={() => handleMobileNavigation(AppRoutes.CHAT)}
          >
            <ListItemIcon sx={{ minWidth: '35px' }}>
              <ChatIcon />
            </ListItemIcon>
            <ListItemText
              primary={
                <div className="flex items-center">
                  <span>Pinnalo AI</span>
                  <span className="ml-2 text-xs bg-blue-100 px-2 py-1 rounded-full flex items-center">
                    <AutoAwesomeIcon sx={{ fontSize: '0.8rem', marginRight: '0.2rem' }} />
                    BETA
                  </span>
                </div>
              }
              primaryTypographyProps={{
                fontSize: '1rem',
                fontWeight: 500,
              }}
            />
          </ListItem>

          <ListItem
            component="button"
            sx={{ paddingBottom: '10px' }}
            onClick={() => handleMobileNavigation(AppRoutes.FEEDBACK)}
          >
            <ListItemIcon sx={{ minWidth: '35px' }}>
              <ThumbUpIcon />
            </ListItemIcon>
            <ListItemText
              primary="Feedback"
              primaryTypographyProps={{
                fontSize: '1rem',
                fontWeight: 500,
              }}
            />
          </ListItem>

          <ListItem
            component="button"
            sx={{ paddingBottom: '10px' }}
            onClick={() => handleMobileNavigation(AppRoutes.REFERRAL)}
          >
            <ListItemIcon sx={{ minWidth: '35px' }}>
              <ReferralIcon />
            </ListItemIcon>
            <ListItemText
              primary={t('navbar:referral')}
              primaryTypographyProps={{
                fontSize: '1rem',
                fontWeight: 500,
              }}
            />
          </ListItem>

          <ListItem
            component="button"
            sx={{ paddingBottom: '10px' }}
            onClick={() => handleMobileNavigation(AppRoutes.SETTINGS)}
          >
            <ListItemIcon sx={{ minWidth: '35px' }}>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText
              primary={t('navbar:settings')}
              primaryTypographyProps={{
                fontSize: '1rem',
                fontWeight: 500,
              }}
            />
          </ListItem>

          <ListItem
            component="button"
            sx={{ paddingBottom: '10px' }}
            onClick={handleMobileLogout}
          >
            <ListItemIcon sx={{ minWidth: '35px' }}>
              <ExitToAppIcon color="error" />
            </ListItemIcon>
            <ListItemText
              primary={t('navbar:logout')}
              primaryTypographyProps={{
                fontSize: '1rem',
                fontWeight: 500,
                color: '#dc2626'
              }}
            />
          </ListItem>
        </List>

        {/* Footer con language switcher e versione */}
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'start', paddingLeft: '24px' }}>
          <div>
            <LanguageSwitcher className="text-base" />
          </div>
          <Typography variant="caption" color="textSecondary" className="text-center block">
            v0.3.7
          </Typography>
        </div>
      </div>
    </Drawer>
  );

  return (
    <AppBar position="static" className="bg-white shadow-md">
      <Toolbar className="justify-between">
        <div className="flex items-center">
          <a href={AppRoutes.PIN_MAP}><img src={LogoBlack} className='w-24 h-auto object-cover' alt='Logo' /></a>
          <div className="hidden gap-8 ml-10 md:flex space-x-4">
            <Link to={AppRoutes.PIN_MAP} className="text-text-primary hover:text-primary transition duration-500 ease-in-out">
              {t('navbar:pinMap')}
            </Link>
            <Link to={AppRoutes.PINNERS} className="text-text-primary hover:text-primary transition duration-500 ease-in-out">
              Pinners
            </Link>
            {userInfo.is_pinner_pro && (
              <button
                onClick={handleConsultancy}
                className="text-text-primary hover:text-primary transition duration-500 ease-in-out bg-transparent border-none cursor-pointer"
              >
                {t('navbar:consultancy')}
              </button>
            )}
            <Link to={AppRoutes.CHAT} className="text-text-primary md:flex items-center hover:text-primary transition duration-500 ease-in-out">
              Pinnalo AI <span className='ml-1 text-xs'><AutoAwesomeIcon sx={{ fontSize: '1.1rem' }} /> BETA</span>
            </Link>
            <Link to={AppRoutes.FEEDBACK} className="text-text-primary hover:text-primary transition duration-500 ease-in-out">
              Feedback
            </Link>
            <Link to={AppRoutes.REFERRAL} className="text-text-primary hover:text-primary transition duration-500 ease-in-out">
              {t('navbar:referral')}
            </Link>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-2 mr-6">
          <IconButton
            onClick={() => navigate(AppRoutes.BILLING)}
            className='flex items-center gap-1 mt-1'
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.04)',
              }
            }}
          >
            <p className='font-bold text-text-primary md:text-md'>{credits}</p>
          </IconButton>
          <IconButton color="error" onClick={handleNotifications}>
            <Badge variant="dot" color="success" invisible={notificationRead || unreadCount === 0}>
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls={menuId}
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
            className="text-tertiary"
          >
            <div className='relative'>
              <Avatar alt={userInfo.first_name} src={userInfo.profile_picture || 'placeholder.svg'} />
              {userInfo.is_pinner_pro && (
                <div style={{ position: 'absolute', bottom: -2, right: -5, zIndex: 10 }}>
                  <PinnerProIcon className='' width={18} height={18} />
                </div>
              )}
            </div>
          </IconButton>
        </div>
        <div className="md:hidden">
          <IconButton
            size="large"
            aria-label="show more"
            aria-controls={mobileMenuId}
            aria-haspopup="true"
            onClick={handleMobileMenuOpen}
            sx={{ color: 'text-text-tertiary' }}
          >
            <ClearAllIcon />
          </IconButton>
        </div>
      </Toolbar>
      {renderMobileMenu}
      {renderMenu}
    </AppBar>
  );
}

export default Navbar;
